import { Box, Typography } from '@mui/material'
import { Suspense, createContext, useContext, useMemo, useState } from 'react'
import { matchPath, useLocation } from 'react-router-dom'

import LoadingPaperSkeleton from '~/components/loadingPaperSkeleton'
import useGetAgentCentralized from '~/features/agents/hooks/useGetAgentCentralized'
import useSetSettingsSteps from '~/features/agents/hooks/useSetSettingsSteps'
import { EClientPath } from '~/models/api'

type TProps = {
  children?: JSX.Element | null
}

type THeaderRightActionContext = {
  setRightAction: (node: JSX.Element | null) => void
}

const HeaderRightActionContext =
  createContext<THeaderRightActionContext | null>(null)

export const useSettingsHeaderRightAction = () => {
  const ctx = useContext(HeaderRightActionContext)
  if (!ctx) {
    return {
      setRightAction: () => {},
    }
  }
  return ctx
}

function SettingsLayout({ children = null }: TProps) {
  const { pathname } = useLocation()
  const [rightAction, setRightAction] = useState<JSX.Element | null>(null)
  const contextValue = useMemo(() => ({ setRightAction }), [setRightAction])
  const subpageSpecialTitles = [
    { path: EClientPath.settingsApiKeys, title: 'API Key Management' },
  ]
  const specialSubtitle = subpageSpecialTitles.find((specialTitle) =>
    pathname.startsWith(specialTitle.path),
  )
  const staticRoutesWithoutTitle = [
    EClientPath.settingsKnowledge,
    EClientPath.settingsChatbot,
    EClientPath.settingsEmail,
    EClientPath.settingsText,
    EClientPath.settingsPhone,
    EClientPath.settingsCoreBehavior,
    EClientPath.settingsPostConvoSettings,
    EClientPath.settingsAnalysisSettingsV2,
    EClientPath.settingsNotifications,
  ]
  const dynamicRoutesWithoutTitle = [EClientPath.settingsIndividualKnowledgeSet]
  const subpageTitle =
    (specialSubtitle && specialSubtitle.title !== undefined
      ? specialSubtitle.title
      : undefined) ||
    pathname.replace('/', '').split('/')[1]?.replaceAll(/-/g, ' ')

  const showTitle =
    !staticRoutesWithoutTitle.some((route) => route === pathname) &&
    !dynamicRoutesWithoutTitle.some((pattern) =>
      matchPath({ path: pattern, end: false }, pathname),
    ) &&
    subpageTitle

  const isLoadingSetSettings = useSetSettingsSteps()
  const { selectedAgent: agent, isLoading: isLoadingAgent } =
    useGetAgentCentralized()

  if (!agent?.id && !isLoadingAgent) {
    return null
  }

  return (
    <HeaderRightActionContext.Provider value={contextValue}>
      <Suspense
        fallback={
          <>
            <LoadingPaperSkeleton sx={{ height: '2.9rem', mb: 5 }} />
            <LoadingPaperSkeleton sx={{ height: '60vh' }} />
          </>
        }
      >
        {showTitle && (
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              mb: 2,
            }}
          >
            <Typography
              variant="h6"
              component="h2"
              textTransform={specialSubtitle ? 'none' : 'capitalize'}
            >
              {subpageTitle}
            </Typography>
            {rightAction}
          </Box>
        )}

        {isLoadingSetSettings || isLoadingAgent ? (
          <LoadingPaperSkeleton sx={{ height: '60vh' }} />
        ) : (
          children
        )}
      </Suspense>
    </HeaderRightActionContext.Provider>
  )
}

export default SettingsLayout
